import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Users, Filter, Sparkles, AlertCircle, RefreshCw, Award, Star, Shield, Target, DollarSign } from "lucide-react";
import { motion } from "framer-motion";
import TutorCard from "../components/tutors/tutorcard";

export default function Tutors() {
  const [selectedSubject, setSelectedSubject] = useState("All");
  const [tutors, setTutors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const subjects = ["All", "Math", "English", "History", "Biology", "Chemistry", "Physics", "Computer Science"];

  // API URL configuration - automatically detects environment
  const API_URL = import.meta.env.VITE_API_URL || 
                (window.location.hostname === 'localhost' 
                  ? 'http://localhost:5001' 
                  : 'https://api.zenithacademics.com');

  useEffect(() => {
    fetchTutors();
  }, []);

  const fetchTutors = async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await fetch(`https://api.zenithacademics.com/api/tutors/approved`);
      if (!response.ok) {
        throw new Error(`Failed to fetch tutors: ${response.status}`);
      }
      const data = await response.json();
      
      console.log("API Response:", data);
      console.log("Tutors data:", data.data);
      
      if (data.success) {
        setTutors(data.data || []);
      } else {
        setError(data.message || "Failed to load tutors");
      }
    } catch (error) {
      console.error("Error fetching tutors:", error);
      setError("Unable to load tutors. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const filteredTutors = selectedSubject === "All"
    ? tutors
    : tutors.filter(tutor => {
        const tutorSubjects = Array.isArray(tutor.subjects) 
          ? tutor.subjects 
          : (typeof tutor.subjects === 'string' ? JSON.parse(tutor.subjects) : []);
        return tutorSubjects?.includes(selectedSubject);
      });

  const subjectColors = {
    All: "bg-gradient-to-r from-zinc-800 to-slate-600 dark:from-zinc-800 dark:to-slate-700 text-white",
    Math: "bg-gradient-to-r from-blue-500 to-cyan-400 dark:from-blue-700 dark:to-cyan-800 text-white",
    English: "bg-gradient-to-r from-rose-500 to-pink-400 dark:from-rose-700 dark:to-pink-800 text-white",
    History: "bg-gradient-to-r from-orange-500 to-amber-400 dark:from-orange-700 dark:to-amber-800 text-white",
    Biology: "bg-gradient-to-r from-emerald-500 to-lime-400 dark:from-emerald-700 dark:to-lime-800 text-white",
    Chemistry: "bg-gradient-to-r from-pink-500 to-fuchsia-400 dark:from-pink-700 dark:to-fuchsia-800 text-white",
    Physics: "bg-gradient-to-r from-indigo-500 to-sky-400 dark:from-indigo-700 dark:to-sky-800 text-white",
    "Computer Science": "bg-gradient-to-r from-purple-500 to-violet-400 dark:from-purple-700 dark:to-violet-800 text-white"
  };

  if (isLoading) {
    return (
      <div className="min-h-screen py-12 md:py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50
        dark:bg-gradient-to-b dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="inline-block"
          >
            <RefreshCw className="w-16 h-16 text-purple-600 dark:text-red-400" />
          </motion.div>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">Loading tutors...</p>
        </div>
      </div>
    );
  }

  if (error && tutors.length === 0) {
    return (
      <div className="min-h-screen py-12 md:py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50
        dark:bg-gradient-to-b dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-950 flex items-center justify-center px-4">
        <div className="max-w-md p-8 rounded-2xl bg-white/90 dark:bg-zinc-900/90 backdrop-blur-sm shadow-2xl">
          <AlertCircle className="w-16 h-16 text-red-500 dark:text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 text-center">Unable to Load Tutors</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6 text-center">{error}</p>
          <Button
            onClick={fetchTutors}
            className="w-full bg-blue-500 hover:bg-blue-600 dark:bg-red-500 dark:hover:bg-red-600 text-white"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  const parseJSONField = (field) => {
    if (!field) return null;
    if (Array.isArray(field)) return field;
    if (typeof field === 'object') return field;
    try {
      return JSON.parse(field);
    } catch (e) {
      console.warn('Failed to parse JSON field:', field);
      return null;
    }
  };

  return (
    <div className="min-h-screen py-12 md:py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50
      dark:bg-gradient-to-b dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-950 relative overflow-hidden transition-colors duration-300">

      {/* Dark mode background glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-pink-400 dark:bg-red-600/30 rounded-full blur-3xl opacity-20 dark:opacity-30 animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-400 dark:bg-orange-500/30 rounded-full blur-3xl opacity-20 dark:opacity-30 animate-pulse delay-700" />
        <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-purple-400 dark:bg-red-500/20 rounded-full blur-3xl opacity-20 dark:opacity-30 animate-pulse delay-1000" />
      </div>

      {/* Floating shapes */}
      <motion.div
        animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-32 right-20 hidden lg:block"
      >
        <div className="w-20 h-20 bg-yellow-300 dark:bg-amber-500/40 rounded-2xl opacity-30 dark:opacity-40" />
      </motion.div>

      <motion.div
        animate={{ y: [0, 20, 0], rotate: [0, -5, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        className="absolute bottom-40 left-20 hidden lg:block"
      >
        <div className="w-16 h-16 bg-blue-300 dark:bg-red-500/30 rounded-full opacity-30 dark:opacity-40" />
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-5xl md:text-7xl font-extrabold bg-gradient-to-r from-blue-900 via-purple-900 to-blue-900
            bg-clip-text text-transparent mb-6 dark:bg-gradient-to-r dark:from-white dark:via-red-400 dark:to-orange-400">
            Our Elite Tutors
          </h1>

          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed
            dark:text-zinc-300">
            Top-performing <span className="font-bold text-purple-600 dark:text-red-400">college students </span> 
            and recent graduates who understand your challenges because they've conquered them.
          </p>
        </motion.div>

        {/* Premium Value Proposition Section */}
{/* Premium Value Proposition Section - FIXED VERSION */}
<motion.div 
  className="mb-12"
  initial={{ opacity: 0, y: 30 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ delay: 0.3 }}
>
  <div className="relative bg-gradient-to-br from-white via-white/95 to-blue-50/90 dark:from-zinc-900/95 dark:via-zinc-900/90 dark:to-blue-950/30 rounded-3xl p-8 md:p-10 border-2 border-white/40 dark:border-red-500/30 shadow-[0_20px_80px_-15px_rgba(59,130,246,0.2)] dark:shadow-[0_20px_80px_-15px_rgba(239,68,68,0.2)] backdrop-blur-sm"> {/* Removed mb-12 */}
    {/* Decorative corner accents */}
    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-purple-600/10 dark:from-red-500/10 dark:to-orange-600/10 rounded-full -translate-y-12 translate-x-12" />
    <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-br from-amber-500/10 to-yellow-500/10 dark:from-amber-600/10 dark:to-yellow-600/10 rounded-full translate-y-8 -translate-x-8" />
    
    {/* Floating particles */}
    <motion.div
      animate={{ y: [0, -15, 0] }}
      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      className="absolute top-8 left-8 w-3 h-3 bg-blue-400/40 dark:bg-red-400/40 rounded-full"
    />
    <motion.div
      animate={{ y: [0, 10, 0] }}
      transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
      className="absolute bottom-12 right-12 w-2 h-2 bg-purple-400/30 dark:bg-orange-400/30 rounded-full"
    />
    
    <div className="relative z-10">
      {/* Main Header */}
      <div className="text-center mb-10"> {/* Reduced from mb-12 */}
        <motion.div 
          className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 dark:from-red-600 dark:via-orange-600 dark:to-amber-600 text-white px-6 py-3 rounded-full mb-6 shadow-lg border border-white/20"
          whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
        >
          <Target className="w-5 h-5" />
          <span className="font-bold text-sm tracking-wide">OUR DIFFERENTIATOR</span>
        </motion.div>
        
        <h2 className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-blue-800 via-purple-800 to-indigo-800 dark:from-white dark:via-red-300 dark:to-orange-400 bg-clip-text text-transparent mb-4 pb-2">
          Why Our Tutors Are Different
        </h2>
        
        <div className="w-24 h-1.5 bg-gradient-to-r from-blue-400 to-purple-400 dark:from-red-400 dark:to-orange-400 rounded-full mx-auto mb-6" />
        
        <p className="text-xl text-gray-700 dark:text-zinc-300 max-w-3xl mx-auto leading-relaxed">
          We've redefined peer tutoring by focusing on <span className="font-bold text-blue-600 dark:text-red-400">quality over quantity</span>. Every tutor represents the pinnacle of academic achievement and teaching excellence.
        </p>
      </div>

      {/* Value Cards Grid - FIXED MARGIN */}
      <div className="grid md:grid-cols-2 gap-8"> {/* Removed mb-16 */}
        <motion.div 
          className="relative bg-gradient-to-br from-white via-white to-blue-50 dark:from-zinc-900 dark:via-zinc-900 dark:to-blue-900/20 rounded-2xl p-8 border-2 border-blue-200/60 dark:border-blue-800/40 shadow-xl hover:shadow-2xl transition-all duration-300 group overflow-hidden"
          whileHover={{ y: -5 }}
        >
          {/* Card accent line */}
          <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 dark:from-blue-600 dark:via-indigo-600 dark:to-purple-600" />
          
          {/* Floating background shape */}
          <div className="absolute -right-8 -top-8 w-32 h-32 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-full opacity-50 group-hover:opacity-70 transition-opacity" />
          
          <div className="relative z-10">
            <div className="flex flex-col md:flex-row items-start gap-6 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 dark:from-blue-600 dark:to-indigo-700 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Specialized Excellence
                  </h3>
                  <div className="w-3 h-3 bg-blue-500 dark:bg-blue-400 rounded-full animate-pulse" />
                </div>
                <p className="text-gray-700 dark:text-zinc-300 leading-relaxed">
                  We take pride in our selective roster of tutors—advanced university students who only teach subjects in which they possess absolute confidence and mastery. While their time is limited due to their own academic commitments, the caliber of mentorship they provide is unmatched. 
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 bg-blue-50/80 dark:bg-blue-900/20 px-4 py-3 rounded-xl border border-blue-100 dark:border-blue-800/40">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-indigo-500 dark:from-blue-500 dark:to-indigo-600 rounded-lg flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-full" />
              </div>
              <span className="font-semibold text-blue-700 dark:text-blue-300">Mastery-based subject allocation</span>
            </div>
          </div>
        </motion.div>

        <motion.div 
          className="relative bg-gradient-to-br from-white via-white to-amber-50 dark:from-zinc-900 dark:via-zinc-900 dark:to-amber-900/20 rounded-2xl p-8 border-2 border-amber-200/60 dark:border-amber-800/40 shadow-xl hover:shadow-2xl transition-all duration-300 group overflow-hidden"
          whileHover={{ y: -5 }}
        >
          {/* Card accent line */}
          <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 dark:from-amber-600 dark:via-orange-600 dark:to-red-600" />
          
          {/* Floating background shape */}
          <div className="absolute -left-8 -bottom-8 w-32 h-32 bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/20 rounded-full opacity-50 group-hover:opacity-70 transition-opacity" />
          
          <div className="relative z-10">
            <div className="flex flex-col md:flex-row items-start gap-6 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 dark:from-amber-600 dark:to-orange-700 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
                <DollarSign className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Investment in Excellence
                  </h3>
                  <div className="w-3 h-3 bg-amber-500 dark:bg-amber-400 rounded-full animate-pulse" />
                </div>
                <p className="text-gray-700 dark:text-zinc-300 leading-relaxed">
                  We are committed to competitive compensation that reflects our tutors' specialized knowledge and the value of their limited time. This investment ensures that your student learns from top-tier mentors who bring unparalleled expertise and dedication to every session.
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 bg-amber-50/80 dark:bg-amber-900/20 px-4 py-3 rounded-xl border border-amber-100 dark:border-amber-800/40">
              <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 dark:from-amber-500 dark:to-orange-600 rounded-lg flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-full" />
              </div>
              <span className="font-semibold text-amber-700 dark:text-amber-300 whitespace-normal break-words">
                Competitive compensation reflecting expertise
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </div>
</motion.div>

        {/* Subject Filter */}
        <motion.div 
          className="mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 dark:from-red-500 dark:to-orange-600 rounded-xl flex items-center justify-center">
              <Filter className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-gray-900 dark:text-white text-xl">Find Your Perfect Match</span>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {subjects.map((subject) => (
              <motion.div
                key={subject}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  onClick={() => setSelectedSubject(subject)}
                  variant={selectedSubject === subject ? "default" : "outline"}
                  className={`transition-all duration-300 text-base font-bold px-6 py-6 rounded-xl backdrop-blur-sm ${
                    selectedSubject === subject
                      ? `${subjectColors[subject]} text-white shadow-2xl border-0 dark:shadow-red-700/30`
                      : "hover:bg-gray-100 border-2 border-gray-300 dark:border-red-500/40 dark:bg-zinc-900/90 dark:hover:bg-zinc-800/50 dark:text-zinc-300"
                  }`}
                >
                  {selectedSubject === subject && (
                    <Sparkles className="w-4 h-4 mr-2 text-yellow-300 dark:text-red-400 animate-pulse" />
                  )}
                  {subject}
                </Button>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Tutors Grid */}
        {filteredTutors.length > 0 ? (
          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            {filteredTutors.map((tutor, index) => {
              const tutorSubjects = parseJSONField(tutor.subjects) || [];
              const tutorAvailability = parseJSONField(tutor.availability) || {};
              
              return (
                <motion.div
                  key={tutor.id || index}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 + 0.7 }}
                >
                  <TutorCard tutor={{
                    id: tutor.id,
                    name: tutor.full_name || tutor.name || "Tutor",
                    current_school: tutor.current_school || "University Student",
                    education: tutor.education || "",
                    subjects: tutorSubjects,
                    experience: tutor.experience || "Experienced tutor passionate about helping students succeed.",
                    availability: tutorAvailability,
                    rating: tutor.rating || 4.5,
                    students: tutor.students_count || 0,
                    photo_url: tutor.photo_url || null
                  }} />
                </motion.div>
              );
            })}
          </motion.div>
        ) : (
          <motion.div 
            className="text-center py-20"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7 }}
          >
            <div className="w-32 h-32 bg-gradient-to-br from-gray-200 to-gray-300 dark:bg-gradient-to-br dark:from-red-500/30 dark:to-orange-500/30 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl">
              <Users className="w-16 h-16 text-gray-400 dark:text-orange-400" />
            </div>
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">No tutors found</h3>
            <p className="text-xl text-gray-600 dark:text-zinc-400">
              {selectedSubject === "All" 
                ? "Currently there are no approved tutors available. Please check back later."
                : `No tutors available for ${selectedSubject} yet. Try another subject!`
              }
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
}